import { useState, useEffect } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

function App() {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [blogs, setBlogs] = useState([]);
  const [showForm, setShowForm] = useState(false);

  // Color palette for cards
  const cardColors = [
    'from-red-400 to-pink-500',
    'from-blue-400 to-cyan-500',
    'from-green-400 to-emerald-500',
    'from-yellow-400 to-orange-500',
    'from-purple-400 to-pink-500',
    'from-indigo-400 to-blue-500',
    'from-rose-400 to-red-500',
    'from-teal-400 to-cyan-500',
    'from-lime-400 to-green-500',
    'from-amber-400 to-orange-500',
  ];

  const getCardColor = (index) => {
    return cardColors[index % cardColors.length];
  };

  const fetchBlogs = async () => {
    const res = await axios.get('https://qs9maczlpl.execute-api.ap-south-1.amazonaws.com/blogs');
    setBlogs(res.data);
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const saveBlog = async () => {
    if (!title.trim() || !content.trim()) {
      alert('Please fill in all fields');
      return;
    }
    await axios.post('https://qs9maczlpl.execute-api.ap-south-1.amazonaws.com/blogs', {
      title,
      content,
    });
    setTitle('');
    setContent('');
    setShowForm(false);
    fetchBlogs();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            My Blog Portfolio
          </h1>
          <p className="text-gray-600 mt-2">Share your thoughts and ideas with the world</p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Create Blog Section */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-12">
          <button
            onClick={() => setShowForm(!showForm)}
            className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-lg font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-200"
          >
            {showForm ? '✕ Close' : '+ New Blog Post'}
          </button>

          {showForm && (
            <div className="mt-8 space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Blog Title
                </label>
                <input
                  type="text"
                  placeholder="Enter your blog title..."
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Content (Markdown)
                </label>
                <textarea
                  rows="12"
                  placeholder="Write your blog in markdown format..."
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition font-mono text-sm"
                />
              </div>

              <div className="flex gap-4">
                <button
                  onClick={saveBlog}
                  className="bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-200"
                >
                  ✓ Publish Blog
                </button>
                <button
                  onClick={() => {
                    setShowForm(false);
                    setTitle('');
                    setContent('');
                  }}
                  className="bg-gray-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-600 transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Blog List Section */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Latest Posts</h2>
          
          {blogs.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-12 text-center">
              <p className="text-gray-500 text-lg">No blog posts yet. Be the first to write one!</p>
            </div>
          ) : (
            <div className="grid gap-8 md:grid-cols-1 lg:grid-cols-2">
              {blogs.map((blog, index) => (
                <article
                  key={blog.blogId}
                  className={`bg-gradient-to-br ${getCardColor(index)} rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 overflow-hidden p-8 text-white min-h-full`}
                >
                  {/* Color overlay for better readability */}
                  <div className="absolute inset-0 bg-black/10 rounded-xl"></div>
                  
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-2xl font-bold text-white hover:text-gray-100 transition flex-1">
                        {blog.title}
                      </h3>
                      <span className="bg-white/30 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-semibold">
                        Post
                      </span>
                    </div>
                    
                    <div className="prose-colorful max-w-none text-white">
                      <div className="line-clamp-6 markdown-content">
                        <ReactMarkdown>{blog.content}</ReactMarkdown>
                      </div>
                    </div>
                    
                    <div className="mt-6 pt-4 border-t border-white/30 flex items-center justify-between">
                      <span className="text-sm font-semibold text-white/90">Read More →</span>
                      <span className="text-xs bg-white/20 px-2 py-1 rounded">New</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white text-center py-8 mt-16">
        <p className="text-gray-400">© 2024 My Blog Portfolio | Made with React & Tailwind CSS</p>
      </footer>
    </div>
  );
}

export default App;